<script setup lang="ts">
/*
 * Storefront footer — flat: one line of facts, link columns, payment-method
 * marks (PaymentMethodMark), company block and copyright. Company block and social links come
 * from the CMS (useCompanyInfo / fetchSocialLinks); everything else is
 * static navigation.
 */
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import logoUrl from '../assets/logo.svg';
import { useCompanyInfo } from '../composables/useCompanyInfo';
import { fetchSocialLinks, type SocialLink } from '../api/cms';
import { usePaymentMethods } from '../composables/usePaymentMethods';
import { socialIcon } from '../utils/brand-icons';
import BrandMark from './BrandMark.vue';
import PaymentMethodMark from './PaymentMethodMark.vue';
import FooterFacts from './shell/FooterFacts.vue';
import { PLATFORM_LINKS, PREORDER_LINK, PRICE_BUCKETS, platformLink, priceBucketLink } from './shell/navData';
import { usePlatformFacets } from './shell/usePlatformFacets';

const { methods: paymentMethods } = usePaymentMethods();
const platformFacets = usePlatformFacets();
const year = new Date().getFullYear();
const { company } = useCompanyInfo();
const socialLinks = ref<SocialLink[]>([]);

onMounted(async () => {
  try { socialLinks.value = await fetchSocialLinks(); } catch {}
});

const companyName = computed(() => company.value?.name || 'KeyzoStore');
const regLabel = computed(() => {
  if (!company.value?.registrationNumber) return '';
  return `reg.nr. ${company.value.registrationNumber}`;
});
const hasAddress = computed(() =>
  company.value && (company.value.addressLine1 || company.value.city || company.value.country),
);

const LINK = 'text-text-dim transition-colors hover:text-accent';
const COLUMN_TITLE = 'text-eyebrow mb-4';
</script>

<template>
  <footer class="mt-20 border-t border-border bg-bg-base">
    <div class="mx-auto max-w-page px-4 py-6 md:px-8">
      <FooterFacts />
    </div>

    <div class="mx-auto grid max-w-page gap-10 border-t border-border px-4 py-12 md:px-8 lg:grid-cols-[1.4fr_repeat(4,1fr)]">
      <div class="flex flex-col gap-4">
        <RouterLink to="/" aria-label="KeyzoStore — home" class="inline-flex">
          <img :src="logoUrl" alt="KeyzoStore" width="178" height="31" class="h-[30px] w-auto" />
        </RouterLink>
        <p class="max-w-[300px] text-[14px] leading-[22px] text-text-dim">{{ $t('footer.tagline') }}</p>
        <address v-if="company && hasAddress" class="max-w-[300px] text-[12px] not-italic leading-[20px] text-text-faint">
          {{ companyName }}<template v-if="regLabel">, {{ regLabel }}</template><br />
          <template v-if="company.addressLine1">{{ company.addressLine1 }}<br /></template>
          <template v-if="company.city || company.postalCode">{{ [company.city, company.postalCode].filter(Boolean).join(' ') }}<br /></template>
          <template v-if="company.country">{{ company.country }}</template>
        </address>
        <div v-if="socialLinks.length" class="mt-1 flex items-center gap-2">
          <a
            v-for="link in socialLinks"
            :key="link.id"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="link.platform"
            class="btn-icon text-text-dim hover:text-accent"
          >
            <BrandMark v-if="socialIcon(link.platform)" :icon="socialIcon(link.platform)!" class="h-4 w-4" aria-hidden="true" />
            <span v-else class="text-[11px] font-semibold">{{ link.platform.slice(0, 2).toUpperCase() }}</span>
          </a>
        </div>
      </div>

      <div>
        <h4 :class="COLUMN_TITLE">{{ $t('footer.nav.shop') }}</h4>
        <ul class="space-y-2.5 text-[14px]">
          <li><RouterLink to="/catalog" :class="LINK">{{ $t('footer.nav.catalog') }}</RouterLink></li>
          <li><RouterLink :to="PREORDER_LINK" :class="LINK">{{ $t('header.nav.preorders') }}</RouterLink></li>
          <li v-for="b in PRICE_BUCKETS" :key="b.key"><RouterLink :to="priceBucketLink(b)" :class="LINK">{{ $t(`header.price.${b.key}`) }}</RouterLink></li>
        </ul>
      </div>

      <div>
        <h4 :class="COLUMN_TITLE">{{ $t('footer.nav2.platforms') }}</h4>
        <ul class="space-y-2.5 text-[14px]">
          <li v-for="p in PLATFORM_LINKS" :key="p.icon"><RouterLink :to="platformLink(p, platformFacets)" :class="LINK">{{ p.name }}</RouterLink></li>
        </ul>
      </div>

      <div>
        <h4 :class="COLUMN_TITLE">{{ $t('footer.nav2.account') }}</h4>
        <ul class="space-y-2.5 text-[14px]">
          <li><RouterLink to="/account" :class="LINK">{{ $t('footer.nav2.dashboard') }}</RouterLink></li>
          <li><RouterLink to="/orders" :class="LINK">{{ $t('footer.nav2.orders') }}</RouterLink></li>
          <li><RouterLink to="/favorites" :class="LINK">{{ $t('footer.nav2.wishlist') }}</RouterLink></li>
          <li><RouterLink to="/account/billing" :class="LINK">{{ $t('header.menu.billing') }}</RouterLink></li>
        </ul>
      </div>

      <div>
        <h4 :class="COLUMN_TITLE">{{ $t('footer.nav.support') }}</h4>
        <ul class="space-y-2.5 text-[14px]">
          <li><RouterLink to="/help" :class="LINK">{{ $t('footer.nav.faq') }}</RouterLink></li>
          <!-- Support = write to us; FAQ already covers /help. No email in the CMS, no link. -->
          <li v-if="company?.email"><a :href="`mailto:${company.email}`" :class="LINK">{{ $t('footer.nav.supportLink') }}</a></li>
          <li><RouterLink to="/legal/terms" :class="LINK">{{ $t('footer.nav.termsOfService') }}</RouterLink></li>
          <li><RouterLink to="/legal/privacy" :class="LINK">{{ $t('footer.nav.privacyPolicy') }}</RouterLink></li>
          <li><RouterLink to="/legal/cookies" :class="LINK">{{ $t('footer.nav.cookiePolicy') }}</RouterLink></li>
        </ul>
      </div>
    </div>

    <div class="border-t border-border">
      <div class="mx-auto flex max-w-page flex-col justify-between gap-4 px-4 py-5 md:px-8 lg:flex-row lg:items-center">
        <div class="flex flex-wrap items-center gap-2">
          <span class="mr-1 text-[12px] text-text-faint">{{ $t('footer.payments') }}</span>
          <span v-for="m in paymentMethods" :key="m.slug" class="chip !h-7 !bg-bg-elev-2 !text-text-dim" :title="m.name"><PaymentMethodMark v-bind="m" /></span>
        </div>
        <p class="text-[12px] text-text-faint">{{ $t('footer.copyright', { year }) }}</p>
      </div>
    </div>
  </footer>
</template>
